import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiEye,
  FiHeart,
  FiBookmark,
  FiMessageCircle,
} from "react-icons/fi";
import { IconButton } from "@chakra-ui/react";
import { useSession } from "./session";
import { formatCount } from "./api";
import useTypewriter from "./useTypewriter";

const FALLBACK = "Explore this character's story.";
const BLURB_LIMIT = 180;

function clip(text) {
  const chars = Array.from(text);
  return chars.length > BLURB_LIMIT
    ? chars.slice(0, BLURB_LIMIT).join("").trimEnd() + "…"
    : text;
}

function Stats({ stats }) {
  return (
    <div className="card-stats">
      <span>
        <FiHeart aria-hidden="true" />
        {formatCount(stats.likes)}
        <span className="sr-only"> likes</span>
      </span>
      <span>
        <FiEye aria-hidden="true" />
        {formatCount(stats.visits)}
        <span className="sr-only"> visits</span>
      </span>
    </div>
  );
}

export default function CharacterCard({ character: c, index = 0 }) {
  const location = useLocation();
  const { favorites, favoriteBusy, toggleFavorite } = useSession();
  const saved = favorites.has(c.publicId);
  const [active, setActive] = useState(false);
  const [broken, setBroken] = useState(false);
  const description = c.description?.trim() || FALLBACK;
  const { typed, rest, done } = useTypewriter(clip(description), active);
  const tags = c.tags?.slice(0, 2) || [];
  const spotlight = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - box.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - box.top}px`);
  };
  return (
    <article
      className={`character-card${active ? " is-active" : ""}`}
      style={{ "--i": Math.min(index, 11) }}
      onPointerEnter={(e) => e.pointerType !== "touch" && setActive(true)}
      onPointerLeave={() => setActive(false)}
      onPointerMove={spotlight}
      onFocus={() => setActive(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setActive(false);
      }}
    >
      <Link
        className="character-card-link"
        to={`/character/${c.publicId}`}
        state={{ from: location.pathname + location.search }}
        aria-label={`View ${c.name}`}
      >
        <div className="character-cover">
          {broken ? (
            <span className="cover-fallback" aria-hidden="true">
              {Array.from(c.name || "?")[0]}
            </span>
          ) : (
            <img
              src={c.card}
              alt={c.name}
              width="416"
              height="608"
              loading="lazy"
              onError={() => setBroken(true)}
            />
          )}
        </div>
        <div className="card-info">
          <h2>{c.name}</h2>
          <p className="card-summary">{description}</p>
          <div className="card-meta">
            <Stats stats={c.stats} />
            {tags[0] && <span className="card-tag">{tags[0]}</span>}
          </div>
        </div>
        <div className="card-hover" aria-hidden="true">
          <div className="card-hover-head">
            <span className="card-avatar">
              {!broken && <img src={c.thumb || c.card} alt="" loading="lazy" />}
            </span>
            <span className="card-hover-title">
              <strong>{c.name}</strong>
              <small>@{c.author || "creator"}</small>
            </span>
          </div>
          <p className={`card-typed${done ? " is-done" : ""}`}>
            {typed}
            <span className="card-caret" />
            <span className="card-typed-rest">{rest}</span>
          </p>
          {tags.length > 0 && (
            <div className="card-hover-tags">
              {tags.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
          )}
          <span className="card-cta">
            <FiMessageCircle />
            Start chatting
          </span>
        </div>
      </Link>
      {!c.catalogOnly && (
        <IconButton
          className="save-card"
          aria-label={`${saved ? "Unsave" : "Save"} ${c.name}`}
          aria-pressed={saved}
          isLoading={favoriteBusy.has(c.publicId)}
          icon={<FiBookmark fill={saved ? "currentColor" : "none"} />}
          onClick={() => toggleFavorite(c.publicId)}
        />
      )}
    </article>
  );
}
